import type { JwtData } from '@tsndr/cloudflare-worker-jwt'
import { decode } from '@tsndr/cloudflare-worker-jwt'
import { parse, parseSetCookie, serialize } from 'cookie-es'
import { appendResponseHeader } from 'h3'

export default defineNuxtRouteMiddleware(async (to) => {
    const nuxtApp = useNuxtApp()
    const runtimeConfig = useRuntimeConfig()

    // Don't run on client hydration when server rendered
    if (
        import.meta.client &&
        nuxtApp.isHydrating &&
        nuxtApp.payload.serverRendered
    ) {
        return
    }

    const {
        session,
        clear: clearSession,
        fetch: fetchSession
    } = useUserSession()

    const serverEvent = useRequestEvent()
    const accessToken = session.value?.tokens?.accessToken
    const accessPayload = accessToken ? decode(accessToken) : null

    if (!accessPayload && to.meta.auth) {
        useAuth().setPreviousRoute(to)

        return navigateTo(useAuth().loginRoute, {
            external: true
        })
    }

    if (isExpired(accessPayload)) {
        await useRequestFetch()('/auth/refresh', {
            onResponse({
                response: { headers }
            }: {
                response: { headers: Headers }
            }) {
                // Forward the Set-Cookie header to the main server event
                if (import.meta.server && serverEvent) {
                    for (const setCookie of headers.getSetCookie()) {
                        appendResponseHeader(
                            serverEvent,
                            'Set-Cookie',
                            setCookie
                        )
                        // Update session cookie for next fetch requests
                        const { name, value } = parseSetCookie(setCookie)

                        if (name === runtimeConfig.session.name) {
                            // console.log('updating headers.cookie to', value)
                            const cookies = parse(
                                serverEvent.headers.get('cookie') || ''
                            )
                            // set or overwrite existing cookie
                            cookies[name] = value
                            // update cookie event header for future requests
                            serverEvent.headers.set(
                                'cookie',
                                Object.entries(cookies)
                                    .map(([name, value]) =>
                                        serialize(name, value)
                                    )
                                    .join('; ')
                            )

                            // Also apply to serverEvent.node.req.headers
                            if (serverEvent.node?.req?.headers) {
                                serverEvent.node.req.headers['cookie'] =
                                    serverEvent.headers.get('cookie') || ''
                            }
                        }
                    }
                }
            },
            async onResponseError({ response }: { response: Response }) {
                if (response.status === 401 || response.status === 403) {
                    await clearSession()
                    await navigateTo(useAuth().loginRoute, {
                        external: true
                    })
                }
            }
        })
        // refresh the session
        await fetchSession()
    }
})

function isExpired(payload: JwtData | null) {
    return payload?.payload?.exp && payload.payload.exp < Date.now() / 1000
}
